import {
    Alert,
    Box,
    Card,
    CardContent,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';

import { Icon } from '../../shared/Icon';
import { PlannedGiftCard } from './PlannedGiftCard';

import { useJiraIssues } from '../../hooks/useJiraApi';

export interface GiftPlannerBoardProps {
    statuses: Array<'open' | 'done'>;
}

export const GiftPlannerBoard = ({ statuses }: GiftPlannerBoardProps) => (
    <Stack spacing={3}>
        {statuses.map((status) => (
            <Stack key={status} spacing={1.5}>
                {statuses.length > 1 && (
                    <Typography variant="subtitle2" color="text.secondary">
                        {STATUS_LABELS[status]}
                    </Typography>
                )}
                <PlannedGiftsSection status={status} />
            </Stack>
        ))}
    </Stack>
);

const StoryCardSkeleton = () => (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="30%" />
            <Skeleton
                variant="rectangular"
                height={120}
                sx={{ mt: 2, borderRadius: 1 }}
            />
        </CardContent>
    </Card>
);

const PlannedGiftsSection = ({ status }: { status: 'open' | 'done' }) => {
    const query = useJiraIssues(status);

    if (query.isLoading) {
        return (
            <Grid container spacing={2}>
                {[0, 1].map((i) => (
                    <Grid key={i} size={{ xs: 12, lg: 6 }}>
                        <StoryCardSkeleton />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (query.isError) {
        return <Alert severity="error">Failed to load issues</Alert>;
    }

    if (!query.data || query.data.length === 0) {
        return (
            <Paper
                variant="outlined"
                sx={{
                    p: 6,
                    textAlign: 'center',
                    color: 'text.secondary',
                    borderRadius: 2,
                    borderStyle: 'dashed',
                }}
            >
                <Box
                    sx={{
                        fontSize: 48,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Icon name="event_busy" />
                </Box>
                <Typography sx={{ mt: 1 }}>
                    No {status === 'done' ? 'completed' : 'ongoing'} gift plans
                </Typography>
            </Paper>
        );
    }

    return (
        <Grid container spacing={2}>
            {query.data.map((issue) => (
                <Grid key={issue.key} size={{ xs: 12, sm: 6, md: 4 }}>
                    <PlannedGiftCard issue={issue} />
                </Grid>
            ))}
        </Grid>
    );
};

const STATUS_LABELS: Record<'open' | 'done', string> = {
    open: 'In progress',
    done: 'Done',
};
